import React, { useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

// Helper functions to compute default dates
const getYesterday = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  return yesterday;
};

const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

function DateSelector({ open, onClose, onConfirm, isNewVersion, rowData }) {
  // Use lazy initialization for default values
  const [endDate, setEndDate] = useState(getYesterday);
  const [effectiveDate, setEffectiveDate] = useState(getToday);

  // Derive validation error instead of storing in state
  const validationError = useMemo(() => {
    if (endDate && effectiveDate && effectiveDate <= endDate) {
      return "Effective date must be after the end date of the current rule";
    }
    return "";
  }, [endDate, effectiveDate]);

  // Reset handler - called when dialog closes
  const handleClose = useCallback(() => {
    onClose();
    // Reset dates after closing
    setEndDate(getYesterday());
    setEffectiveDate(getToday());
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    if (!endDate || !effectiveDate) {
      return;
    }

    if (effectiveDate <= endDate) {
      return;
    }

    onConfirm({
      endDate: endDate.toISOString(),
      effectiveDate: effectiveDate.toISOString(),
    });

    // Reset dates after confirming
    setEndDate(getYesterday());
    setEffectiveDate(getToday());
  }, [endDate, effectiveDate, onConfirm]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isNewVersion ? "Update Rule Version" : "Confirm Rule Update"}
      </DialogTitle>

      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ mt: 2 }}>
            {isNewVersion ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Set when the updated rule will take effect:
                </Typography>
                <DatePicker
                  label="New Rule Effective Date"
                  value={effectiveDate}
                  onChange={setEffectiveDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                    },
                  }}
                />
              </Box>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 3 }}>
                  This will create a new version of the rule. The current rule
                  will be ended and a new rule will be created with your
                  changes.
                </Alert>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Set when the current rule will end:
                  </Typography>
                  <DatePicker
                    label="Current Rule End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Set when the new rule will take effect:
                  </Typography>
                  <DatePicker
                    label="New Rule Effective Date"
                    value={effectiveDate}
                    onChange={setEffectiveDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                      },
                    }}
                  />
                </Box>
              </>
            )}

            {validationError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {validationError}
              </Alert>
            )}
          </Box>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!!validationError || !endDate || !effectiveDate}
        >
          Confirm Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DateSelector;
